/**
 * V54 — Global Error Boundary.
 *
 * Catches uncaught render-time errors anywhere in the React tree and shows a
 * friendly fallback UI instead of a blank white screen. Logs the error to the
 * console and (optionally) sends it to /api/client-errors for monitoring.
 *
 * Without this, a single missing import or runtime ReferenceError (like the
 * Feb-23 Foreign/NRI crash) takes the entire SPA down on every device.
 */
import { Component } from "react";
import { RefreshCw, Home, AlertTriangle, ExternalLink } from "lucide-react";

export default class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error("[ErrorBoundary]", error, errorInfo);
        this.setState({ errorInfo });

        // Best-effort: report to backend for monitoring (silent fail)
        try {
            const payload = {
                message: error?.message || String(error),
                stack: error?.stack || "",
                component_stack: errorInfo?.componentStack || "",
                url: typeof window !== "undefined" ? window.location.href : "",
                user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
                at: new Date().toISOString(),
            };
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/client-errors`;
            // Use sendBeacon if available (works during unload too)
            if (typeof navigator !== "undefined" && navigator.sendBeacon) {
                navigator.sendBeacon(url, new Blob([JSON.stringify(payload)], { type: "application/json" }));
            } else if (typeof fetch !== "undefined") {
                fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload), keepalive: true }).catch(() => { });
            }
        } catch (_) {
            // noop
        }
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
    };

    handleHome = () => {
        if (typeof window !== "undefined") window.location.href = "/";
    };

    handleReload = () => {
        if (typeof window !== "undefined") window.location.reload();
    };

    render() {
        if (!this.state.hasError) return this.props.children;

        const msg = this.state.error?.message || "Something went wrong.";

        return (
            <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-b from-slate-50 to-white" data-testid="global-error-boundary">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-200 p-6 sm:p-8">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-11 h-11 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
                            <AlertTriangle className="w-6 h-6 text-rose-600" />
                        </div>
                        <h1 className="font-heading text-xl sm:text-2xl font-bold text-[#0F172A]">
                            Oops, something broke
                        </h1>
                    </div>

                    <p className="text-sm text-slate-600 leading-relaxed">
                        Sorry — this page hit an unexpected error. The Anand Tutorials team
                        has been notified automatically. Try refreshing, going back home,
                        or contact us directly if it keeps happening.
                    </p>

                    <details className="mt-3 text-[11px] text-slate-500 bg-slate-50 rounded-lg p-2 border border-slate-200">
                        <summary className="cursor-pointer font-semibold">Technical details</summary>
                        <code className="block mt-2 break-all">{msg}</code>
                    </details>

                    <div className="flex flex-col sm:flex-row gap-2 mt-5">
                        <button
                            onClick={this.handleReload}
                            data-testid="error-boundary-reload"
                            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#1E3A8A] text-white text-sm font-semibold hover:bg-[#1E40AF] transition"
                        >
                            <RefreshCw className="w-4 h-4" /> Refresh page
                        </button>
                        <button
                            onClick={this.handleHome}
                            data-testid="error-boundary-home"
                            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition"
                        >
                            <Home className="w-4 h-4" /> Back to home
                        </button>
                    </div>

                    <a
                        href="https://wa.me/917000530316"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="error-boundary-whatsapp"
                        className="mt-3 text-xs text-slate-500 hover:text-emerald-600 inline-flex items-center gap-1"
                    >
                        Need help? WhatsApp us <ExternalLink className="w-3 h-3" />
                    </a>
                </div>
            </div>
        );
    }
}
